/**
 * ProgrammeTableView component - Table-based view for programmes list
 * 
 * Displays programmes in a sortable table format with columns for:
 * - Programme name and description
 * - Target completion date
 * - Budget and project count
 * - Completion percentage
 * - Action buttons (View, Edit, Delete)
 */

import React from 'react';
import { Eye, Edit, Trash2 } from 'lucide-react';
import DataTable, { TableColumn } from './DataTable';
import Button from './Button';
import { Programme } from '../types/programme';
import { Project } from '../types/project';

interface ProgrammeTableRow {
  id: string;
  name: string;
  description?: string;
  targetDate?: string;
  totalBudget?: number;
  totalProjects: number;
  completionPercentage: number;
  createdAt: string;
}

interface ProgrammeTableViewProps {
  programmes: Programme[];
  projects: Project[];
  onView: (programmeId: string) => void;
  onEdit: (programme: Programme) => void;
  onDelete: (programmeId: string) => void;
}

const ProgrammeTableView: React.FC<ProgrammeTableViewProps> = ({
  programmes,
  projects,
  onView,
  onEdit,
  onDelete,
}) => {
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return '-';
    return `R${amount.toLocaleString()}`;
  };

  // Transform programmes into table rows
  const tableData: ProgrammeTableRow[] = programmes.map(programme => {
    const programmeProjects = projects.filter(p => p.programme_id === programme.id);
    const totalProjects = programmeProjects.length;
    
    return {
      id: programme.id,
      name: programme.name,
      description: programme.description ?? undefined,
      targetDate: programme.end_date ?? undefined,
      totalBudget: programme.total_budget || 0,
      totalProjects,
      completionPercentage: programme.completion_percentage || 0,
      createdAt: programme.created_at,
    };
  });

  // Define table columns
  const columns: TableColumn<ProgrammeTableRow>[] = [
    {
      key: 'name',
      label: 'Programme Name',
      sortable: true,
      className: 'font-medium text-gray-900 dark:text-white min-w-[200px]',
      render: (row) => (
        <div>
          <div className="font-semibold">{row.name}</div>
          {row.description && (
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
              {row.description}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'totalProjects',
      label: 'Projects',
      sortable: true,
      sortValue: (row) => row.totalProjects,
      className: 'text-center',
      render: (row) => (
        <span className="text-gray-900 dark:text-white font-semibold">
          {row.totalProjects}
        </span>
      ),
    },
    {
      key: 'totalBudget',
      label: 'Total Budget',
      sortable: true,
      sortValue: (row) => row.totalBudget || 0,
      className: 'text-sm',
      render: (row) => (
        <span className="text-gray-700 dark:text-gray-300 font-medium">
          {formatCurrency(row.totalBudget)}
        </span>
      ),
    },
    {
      key: 'targetDate',
      label: 'Target Completion',
      sortable: true,
      sortValue: (row) => row.targetDate || '',
      className: 'text-sm',
      render: (row) => (
        <span className="text-gray-700 dark:text-gray-300">
          {formatDate(row.targetDate)}
        </span>
      ),
    },
    {
      key: 'completionPercentage',
      label: 'Progress',
      sortable: true,
      sortValue: (row) => row.completionPercentage,
      className: 'min-w-[150px]',
      render: (row) => (
        <div className="space-y-1">
          <div className="text-sm font-semibold text-gray-900 dark:text-white">
            {row.completionPercentage}%
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-primary-500 to-secondary-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${row.completionPercentage}%` }}
            />
          </div>
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      className: 'text-right min-w-[180px]',
      render: (row) => {
        const programme = programmes.find(p => p.id === row.id)!;
        return (
          <div className="flex gap-1 justify-end">
            <Button
              variant="secondary"
              size="sm"
              leftIcon={Eye}
              onClick={() => onView(row.id)}
              title="View Dashboard"
            >
              View
            </Button>
            <Button
              variant="primary"
              size="sm"
              leftIcon={Edit}
              onClick={() => onEdit(programme)}
              title="Edit Programme"
            >
              Edit
            </Button>
            <Button
              variant="danger"
              size="sm"
              leftIcon={Trash2}
              onClick={() => onDelete(row.id)}
              title="Delete Programme"
            >
              Delete
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-md border border-white/20 dark:border-gray-700/50 rounded-xl shadow-lg p-6">
      <DataTable
        columns={columns}
        data={tableData}
        emptyMessage="No programmes found"
        striped={true}
        getRowKey={(row) => row.id}
        defaultSortKey="name"
        defaultSortDirection="asc"
        onRowClick={(row) => onView(row.id)}
      />
    </div>
  );
};

export default ProgrammeTableView;
